import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { doc, updateDoc, Timestamp, getDoc, collection, query, where, getDocs, orderBy, writeBatch } from 'firebase/firestore';
import { IoClose, IoAlertCircle } from 'react-icons/io5';
import moment from 'moment';
import { useAuth } from '../../context/AuthContext';

const COLLECTION_MAP = {
    'GASTO': 'gastos',
    'INGRESO_CLASIFICADO': 'gastos',
    'CARGO': 'cargos',
    'PAGO': 'movimientos_bancarios'
};

function EditarTransaccionModal({ id, type, onClose, onSuccess }) {
    const { userData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        fecha: '',
        descripcion: '',
        monto: '',
        residenteId: '',
        cuentaContableId: ''
    });
    const [residentes, setResidentes] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [linkedAsiento, setLinkedAsiento] = useState(null);

    const isPago = type === 'PAGO';
    const isCargo = type === 'CARGO';
    const isGasto = type === 'GASTO' || type === 'INGRESO_CLASIFICADO';
    const showResidentSelect = isPago || isCargo;
    const showAccountSelect = isGasto;
    const collectionName = COLLECTION_MAP[type];

    // Load Residents
    useEffect(() => {
        if (!showResidentSelect || !userData?.condominioId) return;

        const fetchResidents = async () => {
            try {
                const q = query(
                    collection(db, "usuarios"),
                    where("condominioId", "==", userData.condominioId),
                    orderBy("nombre", "asc")
                );
                const snapshot = await getDocs(q);
                const lista = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(u => u.rol !== 'admin');
                setResidentes(lista);
            } catch (e) {
                console.error("Error fetching residents:", e);
            }
        };
        fetchResidents();
    }, [userData, showResidentSelect]);

    // Load Accounts (Catalogo)
    useEffect(() => {
        if (!showAccountSelect || !userData?.condominioId) return;

        const fetchAccounts = async () => {
            try {
                const q = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), orderBy("numero", "asc"));
                const snapshot = await getDocs(q);
                const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setCuentas(list);
            } catch (e) {
                console.error("Error fetching accounts:", e);
            }
        };
        fetchAccounts();
    }, [userData, showAccountSelect]);

    // Fetch Linked Ledger Entry (Asiento)
    useEffect(() => {
        if (!isGasto || !id) return;

        const fetchAsiento = async () => {
            // Find asiento where referenciaId == id
            const q = query(collection(db, "contabilidad_asientos"), where("referenciaId", "==", id));
            const snap = await getDocs(q);
            if (!snap.empty) {
                setLinkedAsiento({ id: snap.docs[0].id, ...snap.docs[0].data() });
            }
        };
        fetchAsiento();
    }, [isGasto, id]);

    // Load Document
    useEffect(() => {
        const fetchDoc = async () => {
            if (!collectionName) {
                alert("Tipo de transacción desconocida");
                onClose();
                return;
            }

            try {
                const docRef = doc(db, collectionName, id);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    const data = snap.data();

                    // Normalize fields
                    const dateVal = data.fecha && data.fecha.toDate ? data.fecha.toDate() : new Date();
                    const descVal = data.descripcion || data.concepto || '';
                    const amountVal = data.monto || 0;
                    const resId = data.residenteId || '';
                    const accId = data.cuentaContableId || '';

                    setFormData({
                        fecha: moment(dateVal).format('YYYY-MM-DD'),
                        descripcion: descVal,
                        monto: amountVal,
                        residenteId: resId,
                        cuentaContableId: accId
                    });
                } else {
                    alert("El registro no existe.");
                    onClose();
                }
            } catch (e) {
                console.error(e);
                alert("Error cargando registro.");
            } finally {
                setLoading(false);
            }
        };
        fetchDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, type, collectionName]);

    const handleSave = async () => {
        if (!formData.fecha || !formData.monto) return alert("Fecha y Monto requeridos");

        // Specific validaton for Pago
        if (isPago) {
            if (!formData.residenteId) return alert("Debes seleccionar un residente para el pago.");
            if (!formData.descripcion) return alert("Debes incluir una descripción.");

            if (!window.confirm("⚠️ ¿Estás seguro de editar un PAGO BANCARIO?\n\nSi cambias el monto o residente, verifica que coincida con el depósito real.")) return;
        }

        setLoading(true);
        try {
            const newDate = moment(formData.fecha).toDate();
            const newMonto = parseFloat(formData.monto);

            const docRef = doc(db, collectionName, id);

            // Prepare updates
            const updates = {
                fecha: Timestamp.fromDate(newDate),
                monto: newMonto
            };

            // Field mapping based on Type
            if (type === 'CARGO') {
                updates.concepto = formData.descripcion;
                // Update denormalized resident info
                if (formData.residenteId) {
                    const selectedRes = residentes.find(r => r.id === formData.residenteId);
                    if (selectedRes) {
                        updates.residenteId = selectedRes.id;
                        updates.nombreResidente = selectedRes.nombre;
                        updates.unidadResidente = selectedRes.numeroCasa || selectedRes.unidad || 'N/A';
                    }
                }
            } else if (type === 'PAGO') {
                updates.descripcion = formData.descripcion;
                if (formData.residenteId) {
                    const selectedRes = residentes.find(r => r.id === formData.residenteId);
                    if (selectedRes) {
                        updates.residenteId = selectedRes.id;
                        // For Pagos, we map the name string to 'residenteIdentificado' usually
                        updates.residenteIdentificado = selectedRes.nombre;
                        updates.estatus = 'identificado';
                    }
                }
            } else {
                // Gastos / Ingresos
                updates.descripcion = formData.descripcion;

                // Account Update Logic
                if (showAccountSelect && formData.cuentaContableId) {
                    updates.cuentaContableId = formData.cuentaContableId;
                    const acc = cuentas.find(c => c.id === formData.cuentaContableId);
                    if (acc) {
                        updates.categoria = `${acc.numero} - ${acc.nombre}`;
                    }

                    // Update Ledger if it exists
                    if (linkedAsiento) {
                        const asientoRef = doc(db, 'contabilidad_asientos', linkedAsiento.id);
                        const newDetalles = linkedAsiento.detalles.map(d => {
                            // Replace the OLD account with the NEW account
                            // We don't rely only on ID match in case it was wrong, but usually expenses have 2 lines.
                            // One line is Bank (Asset/Activo), one is Expense(Gasto) or Income(Ingreso).

                            // If this detail line matches the OLD account, OR if it looks like the P&L account
                            // Strategy: If we are saving a GASTO, we look for the detail that is NOT the bank? 
                            // Or simpler: If the detail.cuentaId was the old `data.cuentaContableId` (from initial load, need to track it), update it.
                            // But we only have `formData`. 

                            // Heuristic: If type is GASTO, update the detail where haber is 0 (Debit side is Expense).
                            // If type is INGRESO, update the detail where debe is 0 (Credit side is Income).

                            // Actually, let's use the field `tipo` from the account if possible, but we might not have the old account loaded.
                            // Safe bet: find the detail that != Bank. But we don't know Bank ID easily here.

                            // Better: We know the PREVIOUS `cuentaContableId` from the `linkedAsiento`? No, mapped from `gastos`.
                            // Let's assume the user is correcting the non-bank, non-cash account.

                            // Let's look at the current `cuentaContableId` we are replacing.
                            // We don't have it stored distinct from formData. 

                            // Let's use the `linkedAsiento.detalles`. 
                            // If GASTO: We look for the detail with `debe > 0`.
                            // If INGRESO: We look for the detail with `haber > 0`.

                            // Note: This assumes simple 2-line entry. 
                            const isExpenseLine = (type === 'GASTO' && d.debe > 0) || (type === 'INGRESO_CLASIFICADO' && d.haber > 0);

                            if (isExpenseLine) {
                                return { ...d, cuentaId: formData.cuentaContableId };
                            }
                            return d;
                        });

                        // Add to batch or just update. We are not using batch yet, let's just do sequential await for now. 
                        // To be perfectly safe we'd use batch, but for this task updateDoc(asiento) is fine.
                        await updateDoc(asientoRef, { detalles: newDetalles });
                    }
                }
            }

            await updateDoc(docRef, updates);

            alert("Registro actualizado.");
            if (onSuccess) onSuccess();
            onClose();

        } catch (e) {
            console.error(e);
            alert("Error al guardar.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="modal-overlay"><div className="modal-content"><p>Cargando...</p></div></div>;

    return (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
            <div className="modal-content" style={{ maxWidth: '400px' }}>
                <div className="modal-header">
                    <h3>Editar {type === 'CARGO' ? 'Cuota/Cargo' : isPago ? 'Pago Bancario' : 'Gasto/Ingreso'}</h3>
                    <button onClick={onClose}><IoClose /></button>
                </div>

                <div className="modal-body">
                    {/* Warning for Payment Editing */}
                    {isPago && (
                        <div style={{ marginBottom: '15px', padding: '10px', background: '#fff3cd', borderRadius: '5px', fontSize: '0.9em', color: '#856404', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <IoAlertCircle size={24} />
                            <span>
                                <b>¡Precaución!</b> Estás editando un movimiento conciliado.
                            </span>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Fecha</label>
                        <input
                            type="date"
                            className="form-input"
                            value={formData.fecha}
                            onChange={e => setFormData({ ...formData, fecha: e.target.value })}
                        />
                    </div>

                    {showAccountSelect && (
                        <div className="form-group">
                            <label>Cuenta Contable</label>
                            <select
                                className="form-select"
                                value={formData.cuentaContableId}
                                onChange={e => setFormData({ ...formData, cuentaContableId: e.target.value })}
                            >
                                <option value="">-- Seleccionar Cuenta --</option>
                                {cuentas
                                    .filter(c => {
                                        // Filter appropriate accounts
                                        // Gasto -> GASTO or ACTIVO (rare)
                                        // Ingreso -> INGRESO or PASIVO (rare)
                                        if (type === 'GASTO') return c.tipo === 'GASTO' || c.tipo === 'ACTIVO';
                                        if (type === 'INGRESO_CLASIFICADO') return c.tipo === 'INGRESO' || c.tipo === 'PASIVO';
                                        return true;
                                    })
                                    .map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.numero} - {c.nombre}
                                        </option>
                                    ))}
                            </select>
                            {linkedAsiento && <small style={{ color: 'green', fontSize: '0.7em' }}>Linked to Ledger</small>}
                        </div>
                    )}

                    {showResidentSelect && (
                        <div className="form-group">
                            <label>Residente Asignado</label>
                            <select
                                className="form-select"
                                value={formData.residenteId}
                                onChange={e => setFormData({ ...formData, residenteId: e.target.value })}
                            >
                                <option value="">-- Seleccionar Residente --</option>
                                {residentes.map(r => (
                                    <option key={r.id} value={r.id}>
                                        {r.nombre} ({r.numeroCasa || r.unidad || 'Sin Unidad'})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Descripción / Concepto</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.descripcion}
                            onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Monto</label>
                        <input
                            type="number"
                            className="form-input"
                            value={formData.monto}
                            onChange={e => setFormData({ ...formData, monto: e.target.value })}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="primary-btn" onClick={handleSave}>Guardar Cambios</button>
                </div>
                <p style={{ fontSize: '0.8em', color: '#666', marginTop: '10px' }}>
                    Nota: Al guardar, recuerda hacer clic en <b>"Actualizar Datos"</b> para refrescar los reportes.
                </p>
            </div>
        </div>
    );
}

export default EditarTransaccionModal;
